import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { NotificationBanner } from 'src/components/widgets/notificationBanner/notificationBanner';
import { T } from 'src/shared/helpers';
import { usePageTransition } from 'src/shared/navigationTransition';

import styled, { keyframes } from 'styled-components';

// ========== Styled Components ==========

const bannerShake = keyframes`
	0%, 100% { transform: translateX(-50%); }
	10%, 30%, 50%, 70%, 90% { transform: translateX(calc(-50% - 4px)); }
	20%, 40%, 60%, 80% { transform: translateX(calc(-50% + 4px)); }
`;

const DimOverlay = styled.div<{ $zIndex: number }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: ${(props) => props.$zIndex};
    pointer-events: none;
`;

const BlockingOverlay = styled.div<{ $zIndex: number }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: ${(props) => props.$zIndex};
`;

const FixedBannerContainer = styled.div<{ $shake: boolean; $glowing: boolean; $zIndex: number }>`
    position: fixed;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    z-index: ${(props) => props.$zIndex};
    animation: ${(p) => (p.$shake ? bannerShake : 'none')} 0.3s ease-in-out;

    ${NotificationBanner} {
        margin: 0;
        background: rgb(252, 238, 230);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

// ========== Types ==========

export interface UseUnsavedChangesOptions {
    /** Custom message shown in the banner */
    message?: string;
    /** When false, keep dirty tracking but skip navigation blocking */
    enabled?: boolean;
    /** Portal target for the guard banner and overlay. Defaults to document.body. */
    portalTarget?: HTMLElement | null;
    /** Base z-index for the guard overlay. The banner is rendered one level above it. */
    zIndexBase?: number;
}

export interface UseUnsavedChangesReturn<T> {
    /** Whether the current snapshot differs from the captured baseline */
    isDirty: boolean;
    /** Set the baseline to match the current snapshot. Call on initial load and after successful save. */
    resetSnapshot: () => void;
    /** Mutable ref for the save handler — assign your save function to .current */
    saveRef: React.MutableRefObject<(() => Promise<void> | void) | null>;
    /** Show the navigation guard banner for a custom navigation (e.g. back button). Returns true if guard was shown, false if not dirty. */
    guardNavigation: (path?: string | null) => boolean;
    /** Show the navigation guard banner for a local action. Returns true if guard was shown, false if not dirty. */
    guardAction: (action: () => void) => boolean;
    /** Portal-rendered navigation guard — include this in your JSX */
    NavigationGuard: React.ReactNode;
}

// ========== Hook ==========

/**
 * Snapshot-based unsaved changes detection with navigation guard.
 *
 * Each page passes its current state as `currentSnapshot`. The hook compares it
 * against a captured baseline (via `resetSnapshot()`) using deep equality.
 *
 * Usage:
 * 1. Call `resetSnapshot()` when initial data is loaded — this sets the baseline.
 * 2. `isDirty` is automatically computed by comparing current vs baseline.
 * 3. Call `resetSnapshot()` again after a successful save to update the baseline.
 * 4. Assign `saveRef.current` to your save function for the banner's Save button.
 * 5. Include `{NavigationGuard}` in your JSX for the portal-rendered banner.
 */
export function useUnsavedChanges<T = unknown>(currentSnapshot: T, options?: UseUnsavedChangesOptions): UseUnsavedChangesReturn<T> {
    const { message, enabled = true, portalTarget, zIndexBase = 33 } = options ?? {};

    const currentSnapshotRef = useRef<T>(currentSnapshot);
    currentSnapshotRef.current = currentSnapshot;

    const initialSnapshotRef = useRef<T | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const [showBanner, setShowBanner] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);
    const [pendingBack, setPendingBack] = useState(false);
    const [outsideClickCount, setOutsideClickCount] = useState(0);
    const [isShaking, setIsShaking] = useState(false);
    const [isGlowing, setIsGlowing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const pendingActionRef = useRef<(() => void) | null>(null);
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();

    // Snapshot-based dirty detection
    const isDirty = isInitialized ? !isEqual(initialSnapshotRef.current, currentSnapshot) : false;
    const shouldGuardNavigation = enabled && isDirty;
    const shouldGuardNavigationRef = useRef(shouldGuardNavigation);
    shouldGuardNavigationRef.current = shouldGuardNavigation;

    const saveRef = useRef<(() => Promise<void> | void) | null>(null);

    const resetSnapshot = useCallback(() => {
        initialSnapshotRef.current = currentSnapshotRef.current;
        setIsInitialized(true);
    }, []);

    const triggerShake = useCallback(() => {
        setIsShaking(true);
        setIsGlowing(true);
        setTimeout(() => setIsShaking(false), 500);
        setTimeout(() => setIsGlowing(false), 4000);
    }, []);

    // Listen for sidebar/back-button navigation requests
    useEffect(() => {
        const handleNavigateRequest = (e: CustomEvent) => {
            if (!shouldGuardNavigationRef.current) return;
            e.preventDefault();

            const action = typeof e.detail?.action === 'function' ? e.detail.action : null;
            pendingActionRef.current = action;

            if (action) {
                setPendingPath(null);
                setPendingBack(false);
            } else if (e.detail?.isBack) {
                setPendingPath(null);
                setPendingBack(true);
            } else {
                setPendingPath(e.detail?.path ?? null);
                setPendingBack(false);
            }
            setShowBanner(true);
            triggerShake();
            setOutsideClickCount((prev) => prev + 1);
        };

        window.addEventListener('app-navigate-request', handleNavigateRequest as EventListener);
        return () => {
            window.removeEventListener('app-navigate-request', handleNavigateRequest as EventListener);
        };
    }, [triggerShake]);

    const guardNavigation = useCallback(
        (path?: string | null): boolean => {
            if (!shouldGuardNavigationRef.current) return false;
            pendingActionRef.current = null;
            if (path) {
                setPendingPath(path);
                setPendingBack(false);
            } else {
                setPendingBack(true);
                setPendingPath(null);
            }
            setShowBanner(true);
            triggerShake();
            setOutsideClickCount((prev) => prev + 1);
            return true;
        },
        [triggerShake]
    );

    const guardAction = useCallback(
        (action: () => void): boolean => {
            if (!shouldGuardNavigationRef.current) return false;
            pendingActionRef.current = action;
            setPendingPath(null);
            setPendingBack(false);
            setShowBanner(true);
            triggerShake();
            setOutsideClickCount((prev) => prev + 1);
            return true;
        },
        [triggerShake]
    );

    const shouldShowBanner = showBanner && shouldGuardNavigation;
    const showOverlay = shouldShowBanner && outsideClickCount >= 2;

    const completePendingNavigation = useCallback(
        (path: string | null, goBack: boolean) => {
            if (goBack) {
                window.history.back();
            } else if (path) {
                startPageTransition(() => {
                    navigate(path);
                });
            }
        },
        [navigate, startPageTransition]
    );

    const handleDiscard = useCallback(() => {
        const path = pendingPath;
        const goBack = pendingBack;
        const action = pendingActionRef.current;
        // Reset baseline to current so isDirty becomes false
        if (!action) initialSnapshotRef.current = currentSnapshotRef.current;
        setShowBanner(false);
        setOutsideClickCount(0);
        setPendingPath(null);
        setPendingBack(false);
        pendingActionRef.current = null;
        if (action) action();
        else completePendingNavigation(path, goBack);
    }, [pendingPath, pendingBack, completePendingNavigation]);

    const handleDismiss = useCallback(() => {
        setShowBanner(false);
        setOutsideClickCount(0);
        setPendingPath(null);
        setPendingBack(false);
        pendingActionRef.current = null;
    }, []);

    const handleSave = useCallback(async () => {
        if (!saveRef.current) return;
        const path = pendingPath;
        const goBack = pendingBack;
        const action = pendingActionRef.current;
        setIsSaving(true);
        try {
            await saveRef.current();
            // After save, check if still dirty (resetSnapshot wasn't called = validation failed)
            const stillDirty = !isEqual(initialSnapshotRef.current, currentSnapshotRef.current);
            if (stillDirty) return; // Validation failed — keep banner visible
            // Save succeeded — navigate to pending path
            setShowBanner(false);
            setOutsideClickCount(0);
            setPendingPath(null);
            setPendingBack(false);
            pendingActionRef.current = null;
            if (action) action();
            else completePendingNavigation(path, goBack);
        } catch {
            // Save failed — keep banner visible
        } finally {
            setIsSaving(false);
        }
    }, [pendingPath, pendingBack, completePendingNavigation]);

    const navigationGuardPortalTarget = portalTarget ?? document.body;

    const NavigationGuard =
        shouldShowBanner && navigationGuardPortalTarget
            ? createPortal(
                  <>
                      {showOverlay ? <BlockingOverlay $zIndex={zIndexBase} onClick={triggerShake} /> : <DimOverlay $zIndex={zIndexBase} />}
                      <FixedBannerContainer $shake={isShaking} $glowing={isGlowing && showOverlay} $zIndex={zIndexBase + 1}>
                          <NotificationBanner
                              type='WARNING'
                              variant='slim'
                              showCloseButton
                              onClose={handleDismiss}
                              content={
                                  message ||
                                  T._(
                                      'If you leave the page now, your changes will be lost. Please use "Save" to keep the changes or "Discard" to cancel them.',
                                      'Admin'
                                  )
                              }
                              buttons={[
                                  {
                                      label: T._('Discard', 'Admin'),
                                      linkButton: true,
                                      disabled: isSaving,
                                      onClick: handleDiscard
                                  },
                                  {
                                      label: isSaving ? T._('Saving...', 'Admin') : T._('Save', 'Admin'),
                                      disabled: isSaving,
                                      onClick: handleSave
                                  }
                              ]}
                          />
                      </FixedBannerContainer>
                  </>,
                  navigationGuardPortalTarget
              )
            : null;

    return { isDirty, resetSnapshot, saveRef, guardNavigation, guardAction, NavigationGuard };
}
